import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { App, Button, Tabs } from "antd";
import { ReloadOutlined } from "@ant-design/icons";

import { apiGetOptionStrategies } from "api/apiOptionStrategies";
import { fetchAtmOptionSnapshots } from "./atmOptionSnapshot";
import OptionsSignalSection from "components/AiOptionsAnalysis/OptionsSignalSection";
import OptionsStrategiesTable from "./OptionsStrategiesTable";
import LoadedOptionStrategy from "./LoadedOptionStrategy";
import { formatDay } from "./optionStrategyUtils";
import { useTradingRefresh } from "components/Trading/tradingRefresh";

import "components/AiMagician/AiMagician.scss";

// Options strategies: what used to be the Watchlist (now "signals only"
// strategies) plus trading option strategies with signals, positions and P&L.
const OptionsStrategies = () => {
  const { message } = App.useApp();
  const [strategies, setStrategies] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [tabs, setTabs] = useState([]); // [{key, kind: "strategy"|"analysis", row}]
  const [activeTab, setActiveTab] = useState();
  const [snapshots, setSnapshots] = useState({});
  const snapshotRunId = useRef(0);
  // after a change here or in another Trading tab (no new live quotes)
  const { refreshKey, notifyChanged } = useTradingRefresh();
  const isFirstRefresh = useRef(true);
  const loadedRef = useRef(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  // ?instrument=options&strategyId= (links, pending actions) loads it here
  const [searchParams, setSearchParams] = useSearchParams();
  const strategyFromUrl =
    searchParams.get("instrument") === "options" &&
    searchParams.get("strategyId");

  // live ATM call per strategy (same as the old watchlist columns)
  const loadSnapshots = async (rows) => {
    const runId = ++snapshotRunId.current;
    setSnapshots(
      Object.fromEntries(rows.map((row) => [row._id, { isLoading: true }])),
    );
    if (!rows.length) return;
    let result;
    try {
      result = await fetchAtmOptionSnapshots(
        rows.map((row) => ({
          _id: row._id,
          symbol: row.symbol,
          params: {
            minDays: row.params?.minDays,
            targetGainPercent: row.params?.takeProfitPercent,
          },
        })),
      );
    } catch (err) {
      const error = err?.response?.data?.message || err.message || "Failed";
      result = Object.fromEntries(rows.map((row) => [row._id, { error }]));
    }
    if (runId === snapshotRunId.current) setSnapshots(result);
  };

  const fetchStrategies = async ({ withSnapshots = false } = {}) => {
    setIsLoading(true);
    try {
      const data = await apiGetOptionStrategies();
      setStrategies(data);
      setHasLoaded(true);
      if (withSnapshots) loadSnapshots(data);
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Failed to load options strategies",
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStrategies({ withSnapshots: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isFirstRefresh.current) {
      isFirstRefresh.current = false;
      return;
    }
    fetchStrategies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const openTab = (key, kind, row) => {
    setTabs((prev) =>
      prev.find((tab) => tab.key === key)
        ? prev
        : [{ key, kind, row }, ...prev],
    );
    setActiveTab(key);
  };

  const closeTab = (key) => {
    setTabs((prev) => {
      const next = prev.filter((tab) => tab.key !== key);
      if (key === activeTab) setActiveTab(next[0]?.key);
      return next;
    });
  };

  useEffect(() => {
    if (!strategyFromUrl || !hasLoaded) return;
    const next = new URLSearchParams(searchParams);
    next.delete("strategyId");
    setSearchParams(next, { replace: true });
    const row = strategies.find((item) => item._id === strategyFromUrl);
    if (row) {
      openTab(`strategy-${row._id}`, "strategy", row);
      setTimeout(() =>
        loadedRef.current?.scrollIntoView({ behavior: "smooth" }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strategyFromUrl, hasLoaded]);

  const loadedIds = tabs
    .filter((tab) => tab.kind === "strategy")
    .map((tab) => tab.row._id);

  const tabItems = useMemo(
    () =>
      tabs.map(({ key, kind, row }) => {
        if (kind === "strategy") {
          return {
            key,
            label: `${row.symbol} - ${row.name}`,
            children: (
              <LoadedOptionStrategy
                strategyId={row._id}
                snapshot={snapshots[row._id]?.data}
                onChanged={notifyChanged}
              />
            ),
          };
        }
        const model = row.models?.[0] || {};
        return {
          key,
          label: `Analysis: ${row.symbol} - ${model.name || ""}`,
          children: (
            <OptionsSignalSection
              results={{
                _id: key,
                symbol: row.symbol,
                model,
                startDate: row.backtestStartDate
                  ? formatDay(row.backtestStartDate)
                  : dayjs().add(-1, "year").format("YYYY-MM-DD"),
                // the analysis' end date is exclusive: tomorrow = through today
                endDate: dayjs().add(1, "day").format("YYYY-MM-DD"),
                initialParams: {
                  targetGainPercent: row.params?.takeProfitPercent,
                  minDays: row.params?.minDays,
                  excludedEntryWeekdays: row.params?.excludedEntryWeekdays,
                },
              }}
              onTrackingChanged={notifyChanged}
            />
          ),
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tabs, snapshots],
  );

  return (
    <div className="ai-magician-wrapper">
      <p style={{ color: "#999", marginTop: -8 }}>
        Create one from Options Analysis ("Create options strategy"). Every day
        at 15:50 ET each strategy records the early prediction; trading ones
        suggest buys and exits for you to accept, signals-only ones just alert.
      </p>

      <Button
        size="small"
        icon={<ReloadOutlined />}
        style={{ marginBottom: 12 }}
        onClick={() => fetchStrategies({ withSnapshots: true })}
      >
        Refresh live quotes
      </Button>
      <OptionsStrategiesTable
        strategies={strategies}
        isLoading={isLoading}
        loadedIds={loadedIds}
        snapshots={snapshots}
        onLoad={(row) => openTab(`strategy-${row._id}`, "strategy", row)}
        onUnload={(id) => closeTab(`strategy-${id}`)}
        onAnalyze={(row) => openTab(`analysis-${row._id}`, "analysis", row)}
        onChanged={notifyChanged}
      />

      {tabs.length > 0 && (
        <div ref={loadedRef} style={{ minHeight: 800, marginTop: 24 }}>
          <Tabs
            type="editable-card"
            hideAdd
            items={tabItems}
            activeKey={activeTab}
            onChange={setActiveTab}
            onEdit={(key, action) => action === "remove" && closeTab(key)}
          />
        </div>
      )}
    </div>
  );
};

export default OptionsStrategies;
